import { db } from '@backend/db'
import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react'
import { SolidActionButton } from '@components/Buttons/ActionButton'
import { GenericEditModal } from '@components/DataView/GenericEditModal'
import { DefaultModal } from '@components/Modals/DefaultModal'
import { DOT_PHRASE_TEMPLATES, DotPhraseTemplate, FieldMapValue, USERS, WithId } from '@hb/shared'
import { useQuery } from '@hooks/backend/useQuery'
import { addDoc, collection, CollectionReference, deleteDoc, doc } from 'firebase/firestore'
import { useCallback, useMemo, useRef, useState } from 'react'
import { dotPhraseTemplateField } from './fields'

// §E: Personal dot phrase template management modal — list + add + delete.
export const MyDotPhrasesModal = ({
  isOpen,
  onClose,
  userId,
}: {
  isOpen: boolean
  onClose: () => void
  userId: string
}) => {
  const personalRef = useMemo(
    () =>
      collection(db, USERS, userId, DOT_PHRASE_TEMPLATES) as CollectionReference<DotPhraseTemplate>,
    [userId],
  )
  const { dataArr: templates } = useQuery<DotPhraseTemplate>(personalRef)
  const [addOpen, setAddOpen] = useState(false)
  const deletingRef = useRef<Set<string>>(new Set())
  const [, forceRender] = useState(0)

  const handleAdd = useCallback(
    async (values: FieldMapValue) => {
      await addDoc(collection(db, USERS, userId, DOT_PHRASE_TEMPLATES), {
        keyword: (values.keyword as string).trim().toLowerCase(),
        body: values.body as string,
        description: (values.description as string | undefined) ?? '',
        level: 'personal',
        createdOn: Date.now(),
        updatedOn: Date.now(),
      } satisfies DotPhraseTemplate).catch(console.error)
      setAddOpen(false)
      return {}
    },
    [userId],
  )

  const handleDelete = useCallback(
    async (t: WithId<DotPhraseTemplate>) => {
      deletingRef.current.add(t.id)
      forceRender(n => n + 1)
      await deleteDoc(doc(db, USERS, userId, DOT_PHRASE_TEMPLATES, t.id)).catch(console.error)
      deletingRef.current.delete(t.id)
      forceRender(n => n + 1)
    },
    [userId],
  )

  return (
    <>
      <DefaultModal
        isOpen={isOpen}
        onClose={onClose}
        header="My Dot Phrase Templates"
        size="md"
        render={() => (
          <VStack align="stretch" spacing={0} p={2}>
            {!templates || templates.length === 0 ? (
              <Text fontSize="sm" color="gray.500" p={3}>
                No personal templates yet. Click "Add Template" to create one.
              </Text>
            ) : (
              templates.map(t => (
                <Flex
                  key={t.id}
                  align="center"
                  gap={2}
                  px={3}
                  py={2}
                  borderBottomWidth="1px"
                  borderColor="gray.100">
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight={600}>
                      .{t.keyword}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {t.description || t.body}
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    isLoading={deletingRef.current.has(t.id)}
                    onClick={() => handleDelete(t)}>
                    Remove
                  </Button>
                </Flex>
              ))
            )}
          </VStack>
        )}
        renderFooter={({ onClose: close }) => (
          <Flex gap={2}>
            <SolidActionButton size="sm" onClick={() => setAddOpen(true)}>
              Add Template
            </SolidActionButton>
            <Button size="sm" variant="ghost" onClick={close}>
              Close
            </Button>
          </Flex>
        )}
      />
      <GenericEditModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        field={dotPhraseTemplateField}
        onSubmit={handleAdd}
      />
    </>
  )
}
